/**
 * User Management Page
 * 
 * Displays pending users and user list for approval workflow
 * Requirements: 10.1-10.7, 10A.1-10A.7
 */

import React, { useState, useEffect } from 'react';
import { get, post, del } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

export interface PendingUser {
  userId: string;
  username: string;
  email: string;
  createdAt: string;
}

export interface User {
  userId: string;
  username: string;
  email: string;
  status: 'pending' | 'approved';
  isSystemOwner: boolean;
  approvedBy?: string;
  approvedAt?: string;
  createdAt: string;
}

/**
 * User Management Component
 */
export const UserManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'pending' | 'all'>('pending');
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const session = await fetchAuthSession();
      const authHeader = {
        Authorization: `Bearer ${session.tokens?.idToken?.toString()}`
      };

      if (activeTab === 'pending') {
        const response = await get({
          apiName: 'SupplyChainAPI',
          path: '/users/pending',
          options: {
            headers: authHeader
          }
        }).response;

        const data = await response.body.json() as any;
        setPendingUsers(data.pendingUsers || []);
      } else {
        const response = await get({
          apiName: 'SupplyChainAPI',
          path: '/users',
          options: {
            headers: authHeader
          }
        }).response;

        const data = await response.body.json() as any;
        setAllUsers(data.users || []);
      }
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId: string) => {
    if (!confirm('Are you sure you want to approve this user?')) {
      return;
    }

    try {
      const session = await fetchAuthSession();
      const authHeader = {
        Authorization: `Bearer ${session.tokens?.idToken?.toString()}`
      };

      await post({
        apiName: 'SupplyChainAPI',
        path: `/users/${userId}/approve`,
        options: {
          headers: authHeader,
          body: {}
        }
      }).response;

      alert('User approved successfully');
      fetchUsers();
    } catch (err) {
      console.error('Error approving user:', err);
      alert('Failed to approve user');
    }
  };

  const handleDelete = async (userId: string, username: string) => {
    if (!confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const session = await fetchAuthSession();
      const authHeader = {
        Authorization: `Bearer ${session.tokens?.idToken?.toString()}`
      };

      await del({
        apiName: 'SupplyChainAPI',
        path: `/users/${userId}`,
        options: {
          headers: authHeader
        }
      }).response;

      alert('User deleted successfully');
      fetchUsers();
    } catch (err: any) {
      console.error('Error deleting user:', err);
      const errorMessage = err?.response?.body?.error?.message || 'Failed to delete user';
      alert(errorMessage);
    }
  };

  if (loading) {
    return <div className="user-management-page"><p>Loading users...</p></div>;
  }

  if (error) {
    return <div className="user-management-page"><p className="error">{error}</p></div>;
  }

  return (
    <div className="user-management-page">
      <h1>User Management</h1>

      {/* Tabs */}
      <div className="tabs">
        <button 
          className={activeTab === 'pending' ? 'active' : ''}
          onClick={() => setActiveTab('pending')}
        >
          Pending Approvals {pendingUsers.length > 0 && `(${pendingUsers.length})`}
        </button>
        <button 
          className={activeTab === 'all' ? 'active' : ''}
          onClick={() => setActiveTab('all')}
        >
          All Users
        </button>
      </div>

      {/* Pending Users Tab */}
      {activeTab === 'pending' && (
        <div className="pending-users">
          <h2>Pending User Approvals</h2>
          {pendingUsers.length === 0 ? (
            <p>No pending user approvals.</p>
          ) : (
            <table className="user-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Registered</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingUsers.map(user => (
                  <tr key={user.userId}>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button 
                        onClick={() => handleApprove(user.userId)}
                        className="primary-button"
                      >
                        Approve
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* All Users Tab */}
      {activeTab === 'all' && (
        <div className="all-users">
          <h2>All Users</h2>
          <table className="user-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Status</th>
                <th>Role</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {allUsers.map(user => (
                <tr key={user.userId}>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`badge ${user.status}`}>
                      {user.status}
                    </span>
                  </td>
                  <td>
                    {user.isSystemOwner && (
                      <span className="badge system-owner">System Owner</span>
                    )}
                    {!user.isSystemOwner && 'User'}
                  </td>
                  <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                  <td>
                    {!user.isSystemOwner && (
                      <button 
                        onClick={() => handleDelete(user.userId, user.username)}
                        className="danger-button"
                        style={{ padding: '4px 8px', fontSize: '12px' }}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
